import { fakerDE as faker } from '@faker-js/faker';
import {
  ContactType,
  CreateCaseDto,
  CreateDataBlockDictionaryItemDto,
  CreateDoctorDto,
  CreatePatientDto,
  CreateResourceDto,
  CreateSubtenantDto,
  CreateTagDto,
  CreateVisitDto,
  DictionaryRecordDto,
  DoctorType,
  QueryFactory,
  ResourceType,
  SubtenantDto,
} from '@lmt/shared-types';

import { CustomDictionaryName } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { addDays, addHours, startOfDay } from 'date-fns';

export type GenerateDataOptions = {
  generateAdditionalSubtenants: boolean;
};

const generatedSubtenants: CreateSubtenantDto[] = [
  {
    name: 'GeneratedSubtenant1',
    code: 'ge1',
    workingTimeStartInSeconds: 32400,
    workingTimeEndInSeconds: 64800,
  },
  {
    name: 'GeneratedSubtenant2',
    code: 'ge2',
    workingTimeStartInSeconds: 32400,
    workingTimeEndInSeconds: 64800,
  },
  {
    name: 'GeneratedSubtenant3',
    code: 'ge3',
    workingTimeStartInSeconds: 32400,
    workingTimeEndInSeconds: 64800,
  },
];
const generatedSubtenantCodes = generatedSubtenants.map((s) => s.code);

function generateHealthInsuranceCompany(): Omit<
  CreateDataBlockDictionaryItemDto,
  'dictionaryId'
> {
  const shortName = faker.company.name();
  const number = faker.number
    .int({ min: 100000000, max: 999999999 })
    .toString();
  const fullName = `${shortName}, ${number}`;
  return {
    value: fullName,
    values: {
      Number: number,
      'Short name': shortName,
      'Phone number': generatePhoneNumber(),
    },
  };
}

function generateHealthInsuranceCompanies(): Omit<
  CreateDataBlockDictionaryItemDto,
  'dictionaryId'
>[] {
  const companies: Omit<CreateDataBlockDictionaryItemDto, 'dictionaryId'>[] =
    [];
  for (let i = 0; i < 3; i++) {
    companies.push(generateHealthInsuranceCompany());
  }
  return companies;
}

function generateDoctor(doctorType: DoctorType): CreateDoctorDto {
  const firstName = faker.person.firstName();
  const lastName = faker.person.lastName();
  return {
    doctorType: doctorType,
    name: firstName,
    surname: lastName,
    workPhone: generatePhoneNumber(),
    email: removeAccents(
      firstName.toLowerCase() + lastName.toLowerCase() + '@uni-mail.de',
    ),
    active: true,
  };
}

function generateDoctors(): CreateDoctorDto[] {
  const doctors: CreateDoctorDto[] = [];
  for (let i = 0; i < 5; i++) {
    doctors.push(generateDoctor(DoctorType.Clinic));
    doctors.push(generateDoctor(DoctorType.External));
  }
  return doctors;
}

function generatePatient(
  subtenantId: number,
  tags: DictionaryRecordDto[],
): CreatePatientDto {
  const sex = faker.number.int({ min: 1, max: 2 });
  const gender = sex === 1 ? 'male' : 'female';
  const firstName = faker.person.firstName(gender);
  const lastName = faker.person.lastName(gender);
  return {
    firstName: firstName,
    lastName: lastName,
    sex: sex,
    dateOfBirth: faker.date.past({ years: 80, refDate: '2000-01-01' }),
    mobilePhone: generatePhoneNumber(),
    homePhone: generatePhoneNumber(),
    workPhone: generatePhoneNumber(),
    email: removeAccents(
      firstName.toLowerCase() + lastName.toLowerCase() + '@uni-mail.de',
    ),
    subtenantId: subtenantId,
    location: faker.location.city(),
    address: faker.location.streetAddress(),
    priorityContactType: ContactType.MobilePhone,
    postcode: faker.number
      .int({
        min: 10000,
        max: 99999,
      })
      .toString(),
    comments: generateCommentWithTag(tags),
  };
}

function generateCase(patientId: number): CreateCaseDto {
  return {
    patientId: patientId,
    startDate: new Date(),
    endDate: null,
  };
}

function generateVisit(
  patientId: number,
  caseId: number,
  visitTypeIds: number[],
  tags: DictionaryRecordDto[],
  isOvernightVisit: boolean,
): CreateVisitDto {
  const dateNow = startOfDay(new Date());
  const dayOffset = faker.number.int({ min: -5, max: 5 });
  const hourOffset = isOvernightVisit
    ? faker.number.int({ min: 18, max: 23 })
    : faker.number.int({ min: 8, max: 15 });
  const visitLength = isOvernightVisit
    ? faker.number.int({ min: 10, max: 12 })
    : faker.number.int({ min: 1, max: 3 });
  const startDate = addHours(addDays(dateNow, dayOffset), hourOffset);
  const endDate = addHours(startDate, visitLength);

  return {
    caseId,
    title: 'test visit',
    note: generateCommentWithTag(tags),
    start: startDate,
    end: endDate,
    acceptConflict: false,
    typeId: faker.helpers.arrayElement(visitTypeIds),
  };
}

function generatePatients(
  subtenants: SubtenantDto[],
  tags: DictionaryRecordDto[],
): CreatePatientDto[] {
  const patients: CreatePatientDto[] = [];
  const subtenantIds = subtenants.map((s) => s.id);
  for (let i = 0; i < 50; i++) {
    const subtenantId =
      subtenantIds.length === 1
        ? subtenantIds[0]
        : faker.helpers.arrayElement<number>(subtenantIds);
    patients.push(generatePatient(subtenantId, tags));
  }
  return patients;
}

function generatePhoneNumber() {
  return (
    '49' +
    faker.number
      .int({
        min: 1000000000,
        max: 9999999999,
      })
      .toString()
  );
}

function generateResources(): CreateResourceDto[] {
  const resources: CreateResourceDto[] = [];
  Object.keys(ResourceType)
    .map((type) => parseInt(type))
    .filter((type) => !isNaN(type) && type !== ResourceType.Doctors)
    .forEach((type) => {
      for (let i = 0; i < 5; i++) {
        resources.push({
          name: `${ResourceType[type]}${i + 1}`,
          type: type,
        });
      }
    });
  return resources;
}

function generateTags(): CreateTagDto[] {
  const tags: CreateTagDto[] = [];
  for (let i = 0; i < 5; i++) {
    tags.push({
      value: faker.lorem.word(),
    });
  }
  return tags;
}

function generateCommentWithTag(tags: DictionaryRecordDto[]): string {
  const tagIndex = faker.number.int({ min: 0, max: tags.length - 1 });
  const randomTag = tags[tagIndex];
  return faker.lorem.words(5) + ' ' + `[#${randomTag.value}]{${randomTag.id}}`;
}

async function tryCreateVisitWithRoom(
  visit: CreateVisitDto,
  roomIds: number[],
): Promise<boolean> {
  for (const roomId of roomIds) {
    // we try to create visit with room one by one
    // if it fails with status 409 (room is occupied) we take the next room
    visit.resourceIds = [roomId];
    try {
      await QueryFactory.AppointmentEventsClient.createVisit(visit);
      return true;
    } catch (e: any) {
      if (e.status === 409) {
        continue;
      }
      return false;
    }
  }
  return false;
}

export async function generateTestData(options: GenerateDataOptions) {
  let subtenants = (await QueryFactory.TenantClient.getSubtenants()).data;
  if (options.generateAdditionalSubtenants)
    subtenants = subtenants.filter((s) => s.name.startsWith('Gen'));
  else {
    subtenants = subtenants.filter((s) => s.code === 'def');
  }
  const visitTypes = await QueryFactory.VisitTypeClient.get();
  const visitTypeIds = visitTypes.map((x) => x.id);

  const subtenantCodes = subtenants.map((s) => s.code);
  const isNotGenerated =
    options.generateAdditionalSubtenants &&
    generatedSubtenantCodes.every((code) => !subtenantCodes.includes(code));

  if (isNotGenerated) {
    subtenants = [];
    for (const generatedSubtenant of generatedSubtenants) {
      const subtenant =
        await QueryFactory.TenantClient.createSubtenant(generatedSubtenant);
      subtenants.push(subtenant);
    }
  }

  const resources = generateResources();
  for (const resource of resources) {
    try {
      await QueryFactory.ResourceClient.createResource(resource);
    } catch (e) {
      console.log('Error when creating resource', e);
    }
  }

  const tags: DictionaryRecordDto[] = [];
  const tagValues = generateTags();
  for (const tag of tagValues) {
    try {
      const createdTag = await QueryFactory.TagClient.create(tag);
      tags.push(createdTag);
    } catch (e) {
      console.log('Error when creating tag', e);
    }
  }

  const doctors = generateDoctors();
  for (const doctor of doctors) {
    const createdDoctor = await QueryFactory.DoctorClient.createDoctor(doctor);
    if (doctor.doctorType === DoctorType.Clinic) {
      const resourceName =
        `${createdDoctor.surname} ${createdDoctor.name}`.trim();
      await QueryFactory.ResourceClient.createResource({
        name: resourceName,
        type: ResourceType.Doctors,
        doctorId: createdDoctor.id,
      });
    }
  }

  const dataBlockDictionaries =
    await QueryFactory.DataBlockDictionaryClient.get();
  const insuranceCompanyDictionary = dataBlockDictionaries.find(
    (dictionary) =>
      dictionary.title === CustomDictionaryName.InsuranceCompanies,
  );
  if (insuranceCompanyDictionary) {
    const generatedHealthInsuranceCompanies =
      generateHealthInsuranceCompanies();
    for (const healthInsuranceCompany of generatedHealthInsuranceCompanies) {
      await QueryFactory.DataBlockDictionaryItemClient.create({
        ...healthInsuranceCompany,
        dictionaryId: insuranceCompanyDictionary.id,
      });
    }
  }

  const rooms =
    (await QueryFactory.ResourceClient.getResources(false, ResourceType.Places))
      .data ?? [];
  const roomIds = rooms.map((x) => x.id);

  const patients = generatePatients(subtenants, tags);
  for (const patient of patients) {
    const patientResult =
      await QueryFactory.PatientClient.createPatient(patient);
    const patientCase = generateCase(patientResult.id);
    const caseResult = await QueryFactory.CaseClient.createCase(patientCase);

    const isOvernightVisit = faker.datatype.boolean({ probability: 0.6 }); // 30 visits out of 50 should be overnight and have assigned room
    let visit = generateVisit(
      patientResult.id,
      caseResult.id,
      visitTypeIds,
      tags,
      isOvernightVisit,
    );
    if (isOvernightVisit) {
      const result = await tryCreateVisitWithRoom(visit, roomIds);
      if (result) {
        continue;
      } else {
        // if we couldn't create night visit with room (e.g. all rooms are occupied), we create a day visit instead without room
        visit = generateVisit(
          patientResult.id,
          caseResult.id,
          visitTypeIds,
          tags,
          false,
        );
      }
    }
    await QueryFactory.AppointmentEventsClient.createVisit(visit);
  }
}

function removeAccents(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
